import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import posthog from 'posthog-js'
import { useAccount, useDisconnect } from 'wagmi'

import { useWeb3Modal } from '@web3modal/wagmi/react'

import { Address } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger, walletLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores'
import useLandingStore from '@src/zustand_stores/landing.store'

const logger = createContextLogger('AuthProvider')

interface AuthProvider {
    signStatus: boolean
    disconnectHandler: () => void
    connectHandler: () => Promise<void>
}

export const AuthProviderContext = createContext<AuthProvider>({
    signStatus: false,
    disconnectHandler: () => {},
    connectHandler: async () => {},
})

interface Props {
    children: React.ReactElement
}

const useAuth = () => {
    const { address, isConnected, connector } = useAccount()
    const { disconnectAsync } = useDisconnect()

    // Conditionally use either AppKit or Web3Modal
    // const { open: openAppKit } = useAppKit()
    const { open: openWeb3Modal } = useWeb3Modal()
    const openWallet = isFeatureEnabled('USE_NEW_WALLET_PROVIDER') ? openWeb3Modal : openWeb3Modal

    const [signStatus, setSignStatus] = useState<boolean>(!isConnected || !localStorage.getItem('sign'))
    const [connectedManually, setConnectedManually] = useState<boolean>(false)
    const [connectionTimestamp, setConnectionTimestamp] = useState<number | null>(null)
    const { setBoundReferralCode, setGeneratedReferralCode } = useApplicationStore()
    const setConnectedSign = useLandingStore(state => state.setConnectedSign)

    const connectHandler = async () => {
        setConnectedManually(true)
        const connectTime = Date.now()
        setConnectionTimestamp(connectTime)
        await openWallet()

        // When a user connects their wallet
        posthog.capture('wallet_connected', {
            wallet_address: address,
            wallet_type: connector?.name || 'unknown',
            connection_method: 'manual',
            timestamp: new Date().toISOString(),
        })
    }

    const disconnectHandler = useCallback(async () => {
        try {
            if (connectionTimestamp && address) {
                const sessionDurationInMinutes = Math.round((Date.now() - connectionTimestamp) / (1000 * 60))

                // When a user disconnects
                posthog.capture('wallet_disconnected', {
                    wallet_address: address,
                    session_duration: sessionDurationInMinutes,
                    wallet_type: connector?.name || 'unknown',
                })
            }

            localStorage.clear()
            setConnectedSign({ sign: '' })
            setSignStatus(false)
            setBoundReferralCode({ userAddress: '' as Address, code: '' })
            setGeneratedReferralCode({ userAddress: '' as Address, code: '' })
            setConnectionTimestamp(null)
            await disconnectAsync()
        } catch (err) {
            walletLogger.error('Failed to disconnect', { error: err })
        }
    }, [setConnectedSign, address, connectionTimestamp, connector])

    useEffect(() => {
        const sign = localStorage.getItem('sign')
        if (isConnected) {
            if (!connectedManually && !sign) {
                disconnectHandler()
            } else if (sign) {
                setConnectedSign({ sign: sign })
                if (!connectionTimestamp) {
                    setConnectionTimestamp(Date.now())
                }
            }
        }
        setSignStatus(!isConnected || !localStorage.getItem('sign'))
    }, [isConnected, connectedManually])

    useEffect(() => {
        // Check if the current connector is Phantom
        if (connector?.name?.toLowerCase().includes('phantom') && isConnected) {
            logger.warn('Detected Phantom wallet connection attempt - disconnecting...')
            console.log('----- [EVM] disconnecting -----')
            disconnectAsync()
            return
        }

        // Safe logging for debugging
        const safeConnectorLog = connector
            ? {
                  id: connector.id,
                  name: connector.name,
                  type: connector.type,
                  icon: connector.icon,
                  supportsSimulation: connector.supportsSimulation,
              }
            : null

        logger.info('safeConnectorLog', safeConnectorLog)
    }, [connector, disconnectHandler])

    return {
        signStatus,
        disconnectHandler,
        connectHandler,
    }
}

export const AuthProvider = (props: Props) => {
    const value = useAuth()
    return <AuthProviderContext.Provider value={value}>{props.children}</AuthProviderContext.Provider>
}

export const useAuthContext = () => {
    return useContext(AuthProviderContext)
}
